import React from "react";
import {
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle2 } from "lucide-react";
import { ResponsiveDataList } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import {
  formatLanguage,
  formatRetreatDateRange,
  setupStatusLabel,
  statusChip,
} from "../../lifecycle/lifecycleUi";
import { SectionShell } from "../../lifecycle/SectionShell";
import { retreatTitleCell, tooltipCell } from "../../lifecycle/manageRetreatColumns";
import { SetupRetreatCard } from "../cards/SetupRetreatCard";
import { SectionActionButtons } from "../SectionActionButtons";
import { SECTION_IDS } from "../manageRetreatTypes";

export function InSetupSection({
  rows,
  total,
  loading,
  searchValue,
  onSearchChange,
  dataFetchApi,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
  onSetupMail,
}: {
  rows: LifecycleRetreat[];
  total: number;
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  dataFetchApi: (...args: any[]) => void;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
  onSetupMail: (r: LifecycleRetreat) => void;
}) {
  const columns = [
    {
      id: "action",
      name: "Action",
      sortable: false,
      button: true,
      grow: 0,
      cell: (r: LifecycleRetreat) => (
        <SectionActionButtons
          r={r}
          kind="inSetup"
          isSuperAdmin={isSuperAdmin}
          onHistory={onHistory}
          onDelete={onDelete}
          onApprove={onApprove}
          onRestore={onRestore}
        />
      ),
      width: "200px",
    },
    {
      id: "retreatId",
      name: "Retreat Id",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.id,
      width: "120px",
    },
    {
      id: "title",
      name: "Retreat",
      sortable: true,
      grow: 0,
      cell: retreatTitleCell,
      width: "300px",
    },
    {
      id: "language",
      name: "Language",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => formatLanguage(r.language),
      cell: (r: LifecycleRetreat) => tooltipCell(formatLanguage(r.language)),
      width: "150px",
    },
    {
      id: "dateFrom",
      name: "Dates",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) =>
        formatRetreatDateRange(r.dateFrom, r.dateTo),
      cell: (r: LifecycleRetreat) =>
        tooltipCell(formatRetreatDateRange(r.dateFrom, r.dateTo)),
      width: "150px",
    },
    {
      id: "lead",
      name: "Lead",
      sortable: false,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.leadCoupleName || "Not assigned",
      cell: (r: LifecycleRetreat) =>
        tooltipCell(r.leadCoupleName || "Not assigned"),
      width: "180px",
    },
    {
      id: "readinessPercent",
      name: "Publication Readiness",
      sortable: true,
      grow: 0,
      cell: (r: LifecycleRetreat) =>
        tooltipCell(
          <Stack direction="row" spacing={1} alignItems="center">
            <LinearProgress
              variant="determinate"
              value={r.readinessPercent || 0}
              sx={{ width: 100, height: 8, borderRadius: 10 }}
            />
            <Typography variant="caption" fontWeight={700}>
              {r.readinessPercent || 0}%
            </Typography>
          </Stack>,
          `${r.readinessPercent || 0}%`,
        ),
      width: "200px",
    },
    {
      id: "lifecycleStatus",
      name: "Status",
      sortable: true,
      grow: 0,
      cell: (r: LifecycleRetreat) =>
        tooltipCell(statusChip(setupStatusLabel(r)), setupStatusLabel(r)),
      width: "160px",
    },
    {
      id: "sendEmail",
      name: "Send Email",
      sortable: false,
      button: true,
      grow: 0,
      width: "130px",
      cell: (r: LifecycleRetreat) => (
        <Tooltip
          title={
            isSuperAdmin
              ? "Send Email to Lead Couples and Steering Committee"
              : "Send Email to Admin"
          }
        >
          <span onClick={() => onSetupMail(r)} style={{ cursor: "pointer" }}>
            <i
              className="mdi mdi-email-outline font-size action-icons"
              title="Send Email"
              style={{ fontSize: "20px" }}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <SectionShell
      id={SECTION_IDS.inSetup}
      title="Retreats in Setup"
      subtitle="Lead Couples are completing the required publication checklist."
      bgcolor="#edf8ef"
      icon={<CheckCircle2 size={22} color="#2f7c3b" />}
      total={total}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
    >
      <ResponsiveDataList
        data={rows}
        total={total}
        loading={loading}
        isServerSide
        sortServer
        countPerPage={10}
        dataFetchApi={dataFetchApi}
        columns={columns}
        cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        renderCard={(r) => (
          <SetupRetreatCard
            r={r}
            isSuperAdmin={isSuperAdmin}
            onHistory={onHistory}
            onDelete={onDelete}
            onApprove={onApprove}
            onRestore={onRestore}
            onSetupMail={onSetupMail}
          />
        )}
      />
    </SectionShell>
  );
}
